/**
 * visibility.ts — F1c pure visibility resolver.
 *
 * resolveVisibility(blocks, logic, context): Set<blockId>
 *
 * Semantics:
 *   Block with no element-scope rules         → visible (default, no entry in hiddenSet).
 *   Block with SHOW rule, first match TRUE    → visible.
 *   Block with SHOW rule, no match (FALSE)    → hidden by default (conditional-show pattern).
 *   Block with HIDE rule, first match TRUE    → hidden.
 *   Block with HIDE rule, no match (FALSE)    → visible (conditional-hide default).
 *   First-match-wins by position in logic[]   → deterministic; duplicates are ignored.
 *
 * A4 contract (HIDE × ANSWER):
 *   HIDE does NOT clear the answer from EvalContext.answers.
 *   Callers must pass context.answers that INCLUDES hidden blocks' answers
 *   so that other blocks' conditions can still reference them (no oscillation).
 *
 * Pure module: no React/DOM/navigator/payment-SDK imports.
 * Delegates all evaluation to F1a evaluate() — does NOT duplicate operators.
 */

import type { EditorBlock } from '@/store/editorSlice';
import type { Logic, Rule, EvalContext, Condition } from '@/logic-engine';
// WEB-2015: НЕ через бочку `@/logic-engine` — она ре-экспортирует схемы правил
// (`rule.schema.ts` → zod, 175 КБ несжатых), нужные РЕДАКТОРУ правил, а не
// плееру: плеер правила только вычисляет. Типы выше берутся из бочки как были —
// `import type` до сборки не доживает.
import { evaluate, isConditionReady } from '@/logic-engine/evaluate';

/**
 * resolveVisibility — derive the set of block IDs that should NOT be rendered.
 *
 * @param blocks   - Ordered blocks for the current screen.
 * @param logic    - Full logic array from quiz/paywall data (Rule[]).
 * @param context  - EvalContext snapshot; answers must include hidden blocks (A4).
 * @returns Set<string> of blockId values that are hidden (must not render).
 */

/**
 * Ревизия пачки 2 (фикс FB2, 2026-08-22): true, если в дереве групп есть
 * хотя бы одно ГОТОВОЕ (isConditionReady) листовое условие — не просто
 * присутствующее. Пустая группа (в т.ч. только вложенные пустые подгруппы)
 * И группа из одних НЕЗАПОЛНЕННЫХ условий — обе версии «правило не готово»,
 * и в видимости не участвуют вовсе (правило инертно целиком).
 *
 * Было (WEB-1255, десятка 8): предикат смотрел только на наличие листа
 * (`'combinator' in item ? recurse : true`), не проверяя isConditionReady.
 * Из-за этого SHOW-правило с единственным незаполненным условием считалось
 * «имеющим условия» → участвовало в дефолте паттерна conditional-show →
 * блок был СКРЫТ у ВСЕХ посетителей, даже когда evaluate() честно давал
 * false. На последнем экране пейволла (где запасной кнопки «Далее» нет —
 * PaywallScreenWizard.tsx: showAdvanceFallback = !isLastScreen && ...) это
 * создавало тупик: черновое SHOW-правило на платёжном блоке оставляло
 * посетителя без кнопки оплаты и без «Далее».
 */
function hasReadyCondition(group: { items?: unknown[] }): boolean {
  const items = group?.items ?? [];
  for (const item of items) {
    if (item !== null && typeof item === 'object' && 'combinator' in item) {
      if (hasReadyCondition(item as { items?: unknown[] })) return true;
    } else if (item !== null && typeof item === 'object' && isConditionReady(item as Condition)) {
      return true;
    }
  }
  return false;
}

export function resolveVisibility(
  blocks: EditorBlock[],
  logic: Logic,
  context: EvalContext,
): Set<string> {
  // Group element-scope rules by blockId, preserving logic[] positional order
  // (first-match-wins is positional — earlier index in logic[] wins).
  const rulesByBlock = new Map<string, Rule[]>();
  for (const rule of logic) {
    if (rule.trigger_scope !== 'element') continue;
    const existing = rulesByBlock.get(rule.scope_ref);
    if (existing) {
      existing.push(rule);
    } else {
      rulesByBlock.set(rule.scope_ref, [rule]);
    }
  }

  const hiddenSet = new Set<string>();

  for (const block of blocks) {
    // Ревизия пачки 2 (фикс FB2, WEB-1255 продолжение): правило без ни
    // одного ГОТОВОГО условия (ни листьев вовсе, ни только незаполненных)
    // не участвует ни в матчинге, ни в определении ДЕФОЛТА паттерна — иначе
    // SHOW-правило с недописанным условием прятало бы блок у ВСЕХ
    // посетителей (см. hasReadyCondition doc-comment выше). Неготовое
    // правило инертно целиком: блок виден, как будто правила нет.
    const blockRules = rulesByBlock
      .get(block.id)
      ?.filter((rule) => hasReadyCondition(rule.condition_group));
    if (!blockRules || blockRules.length === 0) {
      // No element-scope rules → visible by default.
      continue;
    }

    // Walk rules in positional order; first TRUE match wins (break immediately).
    let matchedAction: 'SHOW' | 'HIDE' | null = null;
    for (const rule of blockRules) {
      try {
        if (evaluate(rule, context)) {
          const t = rule.action.type;
          if (t === 'SHOW' || t === 'HIDE') {
            matchedAction = t;
            break; // first-match-wins
          }
        }
      } catch {
        // fail-open: broken rules are skipped (mirrors decide.ts behavior)
      }
    }

    if (matchedAction === 'HIDE') {
      // Explicit HIDE matched → block is hidden.
      hiddenSet.add(block.id);
    } else if (matchedAction === 'SHOW') {
      // Explicit SHOW matched → block is visible; do not add to hiddenSet.
    } else {
      // No rule matched (matchedAction === null).
      // Default depends on the first rule's action type:
      //   SHOW: conditional-show pattern → hidden when no rule fires.
      //   HIDE: conditional-hide pattern → visible when no rule fires.
      if (blockRules[0].action.type === 'SHOW') {
        hiddenSet.add(block.id);
      }
      // HIDE default: visible → do not add.
    }
  }

  return hiddenSet;
}
